import { AUTH_RESULT_CODES } from '@/constants/authResultCodes';

/** 认证相关业务错误（携带后端 code，不向前端暴露 message） */
export class AuthBizError extends Error {
  readonly code: number;

  constructor(code: number) {
    super('');
    this.name = 'AuthBizError';
    this.code = code;
  }
}

export type AuthErrorScene =
  | 'registerAccount'
  | 'registerEmail'
  | 'loginAccount'
  | 'loginEmail'
  | 'registerCaptcha'
  | 'loginCaptcha'
  | 'resetPasswordSend'
  | 'resetPasswordVerify'
  | 'resetPasswordConfirm';

export function resolveAuthBizErrorCode(error: unknown, fallback = AUTH_RESULT_CODES.SYSTEM_ERROR): number {
  if (error instanceof AuthBizError) {
    return error.code;
  }
  const data = (error as { response?: { data?: { code?: number } } })?.response?.data;
  if (typeof data?.code === 'number') {
    return data.code;
  }
  return fallback;
}

/**
 * 按场景与错误码返回 i18n key（模糊提示，不暴露后端具体文案）。
 */
function isCaptchaTooFrequentCode(code: number): boolean {
  return (
    code === AUTH_RESULT_CODES.LOGIN_EMAIL_CODE_TOO_FREQUENT ||
    code === AUTH_RESULT_CODES.REGISTER_EMAIL_CODE_TOO_FREQUENT ||
    code === AUTH_RESULT_CODES.RESET_PASSWORD_EMAIL_CODE_TOO_FREQUENT
  );
}

function isCaptchaDailyLimitCode(code: number): boolean {
  return (
    code === AUTH_RESULT_CODES.LOGIN_EMAIL_CODE_DAILY_LIMIT ||
    code === AUTH_RESULT_CODES.REGISTER_EMAIL_CODE_DAILY_LIMIT ||
    code === AUTH_RESULT_CODES.RESET_PASSWORD_EMAIL_CODE_DAILY_LIMIT
  );
}

export function getAuthErrorMessageId(code: number, scene: AuthErrorScene): string {
  if (scene === 'registerCaptcha' || scene === 'loginCaptcha' || scene === 'resetPasswordSend') {
    if (isCaptchaTooFrequentCode(code)) {
      return 'auth.error.captcha.tooFrequent';
    }
    if (isCaptchaDailyLimitCode(code)) {
      return 'auth.error.captcha.dailyLimit';
    }
    if (scene === 'registerCaptcha') {
      return 'pages.register.captcha.sendFailed';
    }
    if (scene === 'loginCaptcha') {
      return 'pages.login.captcha.sendFailed';
    }
    return 'pages.forgotPassword.step1.error';
  }

  switch (scene) {
    case 'registerAccount':
      return 'pages.register.accountRegister.errorMessage';
    case 'registerEmail':
      return 'pages.register.emailRegister.errorMessage';
    case 'loginAccount':
      return 'pages.login.accountLogin.errorMessage';
    case 'loginEmail':
      return 'pages.login.emailLogin.errorMessage';
    case 'resetPasswordVerify':
      return 'pages.forgotPassword.step2.error';
    case 'resetPasswordConfirm':
      return 'pages.forgotPassword.step3.error';
    default:
      return 'auth.error.generic';
  }
}

export function formatAuthErrorMessage(
  intl: { formatMessage: (desc: { id: string }) => string },
  error: unknown,
  scene: AuthErrorScene,
  fallbackCode = AUTH_RESULT_CODES.SYSTEM_ERROR,
): string {
  const code = resolveAuthBizErrorCode(error, fallbackCode);
  return intl.formatMessage({ id: getAuthErrorMessageId(code, scene) });
}
